import axios from 'axios'
import { uuidv4 } from '/@/utils/uuid'
import { CURRENT_CONFIG } from './config'
import { message } from 'ant-design-vue'
import router from '/@/router'
import { ELocalStorageKey, ERouterName } from '/@/types/enums'
export * from './type'

const REQUEST_ID = 'X-Request-Id'
function getAuthToken () {
  return localStorage.getItem(ELocalStorageKey.Token)
}

// Inline, one-shot token refresh used by the 401 recovery path below. Uses a
// bare axios call (NOT the shared `instance`) so it can't recurse through this
// interceptor, and hits /token/refresh which is exempt from the backend auth
// interceptor and tolerates an already-expired token.
async function tryRefreshToken (): Promise<string | null> {
  const token = getAuthToken()
  if (!token) {
    return null
  }
  try {
    const res = await axios.post(
      CURRENT_CONFIG.baseURL + '/manage/api/v1/token/refresh',
      {},
      { headers: { [ELocalStorageKey.Token]: token } },
    )
    const newToken = res?.data?.data?.access_token
    if (newToken) {
      localStorage.setItem(ELocalStorageKey.Token, newToken)
      // Re-push into Pilot's native Api component so background (media/STS/ws)
      // calls made by the native layer recover too, not just this web request.
      try {
        const bridge = (window as any).djiBridge
        if (bridge?.apiSetToken) {
          bridge.apiSetToken(newToken)
        }
      } catch (e) {
        // not running inside Pilot — web-only context, nothing to push
      }
      return newToken
    }
  } catch (e) {
    console.error('inline token refresh failed', e)
  }
  return null
}

const instance = axios.create({
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  // timeout: 12000,
})

instance.interceptors.request.use(
  config => {
    config.headers[ELocalStorageKey.Token] = getAuthToken()
    // config.headers[REQUEST_ID] = uuidv4()
    config.baseURL = CURRENT_CONFIG.baseURL
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

instance.interceptors.response.use(
  response => {
    console.info('URL: ' + response.config.baseURL + response.config.url, '\nData: ', response.data, '\nResponse:', response)
    if (response.data.code && response.data.code !== 0) {
      message.error(response.data.message)
    }
    return response
  },
  async err => {
    const requestId = err?.config?.headers && err?.config?.headers[REQUEST_ID]
    if (requestId) {
      console.info(REQUEST_ID, '：', requestId)
    }
    console.info('url: ', err?.config?.url, `【${err?.config?.method}】 \n>>>> err: `, err)

    let description = '-'
    if (err.response?.data && err.response.data.message) {
      description = err.response.data.message
    }
    if (err.response?.data && err.response.data.result) {
      description = err.response.data.result.message
    }
    // @See: https://github.com/axios/axios/issues/383
    if (!err.response || !err.response.status) {
      message.error('The network is abnormal, please check the backend service and try again')
      return
    }
    // 401 → the token expired. Try a single silent refresh + replay before
    // giving up. Don't toast a raw ERROR_CODE for a 401 we're about to recover.
    if (err.response?.status === 401) {
      const original = err.config || {}
      const url = original.url || ''
      const isAuthEndpoint = url.indexOf('/token/refresh') >= 0 || url.indexOf('/login') >= 0
      if (!original._isRetry && !isAuthEndpoint) {
        original._isRetry = true
        const newToken = await tryRefreshToken()
        if (newToken) {
          original.headers = original.headers || {}
          original.headers[ELocalStorageKey.Token] = newToken
          return instance(original)
        }
      }
      // Refresh failed / not possible → fall back to re-login.
      console.error(err.response)
      router.push(ERouterName.PILOT)
      return Promise.reject(err)
    }
    if (err.response?.status !== 200) {
      message.error(`ERROR_CODE: ${err.response?.status}`)
    }
    // if (err.response?.status === 403) {
    //   // window.location.href = '/'
    // }

    return Promise.reject(err)
  },
)

export default instance
